import { useCallback, useMemo, useState } from "react";
import { Dialog } from "@base-ui/react/dialog";
import { IconCheck, IconCode, IconCopy } from "@tabler/icons-react";

type EmbedCodeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  coordinates: [number, number];
  defaultName?: string;
};

function buildEmbedUrl(coordinates: [number, number], name: string): string {
  const [lng, lat] = coordinates;
  const base = `${window.location.origin}/embed?lat=${lat}&lng=${lng}`;
  if (name.trim()) {
    return `${base}&n=${encodeURIComponent(name.trim())}`;
  }
  return base;
}

function buildIframeCode(url: string, name: string): string {
  const linkText = name.trim() ? `Sun path for ${name.trim()}` : "Sun path";
  return `<iframe src="${url}" width="100%" height="180" frameborder="0" style="border:none;border-radius:12px;max-width:420px;" loading="lazy"></iframe>\n<a href="https://sunny.coffee" target="_blank" rel="noopener" style="font-size:11px;color:#94a3b8;text-decoration:none;font-family:sans-serif;">${linkText} by Sunny.coffee</a>`;
}

export function EmbedCodeDialog({ open, onOpenChange, coordinates, defaultName }: EmbedCodeDialogProps) {
  const [name, setName] = useState(defaultName ?? "");
  const [copied, setCopied] = useState(false);

  const embedUrl = useMemo(() => buildEmbedUrl(coordinates, name), [coordinates, name]);
  const iframeCode = useMemo(() => buildIframeCode(embedUrl, name), [embedUrl, name]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(iframeCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the textarea content
      const textarea = document.querySelector<HTMLTextAreaElement>("#embed-code-textarea");
      if (textarea) {
        textarea.select();
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  }, [iframeCode]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-slate-900/40" />
        <Dialog.Viewport className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <Dialog.Popup className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <Dialog.Title className="flex items-center gap-2 text-base font-semibold text-slate-900">
                <IconCode size={18} stroke={2} />
                Embed widget
              </Dialog.Title>
              <Dialog.Close
                aria-label="Close"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-100"
              >
                ×
              </Dialog.Close>
            </div>

            <p className="mt-3 text-sm text-slate-500">
              Copy the code and paste it on your site. The widget shows today's sun path for this location.
            </p>

            <label className="mt-4 block text-xs font-medium text-slate-500">
              Display name (optional)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sunshine Cafe"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
            />

            <label className="mt-4 block text-xs font-medium text-slate-500">
              Embed code
            </label>
            <textarea
              id="embed-code-textarea"
              readOnly
              value={iframeCode}
              rows={3}
              className="mt-1 w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-600 outline-none"
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            />

            <button
              type="button"
              onClick={() => void handleCopy()}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              {copied ? (
                <>
                  <IconCheck size={16} stroke={2} className="text-emerald-400" />
                  Copied!
                </>
              ) : (
                <>
                  <IconCopy size={16} stroke={2} />
                  Copy embed code
                </>
              )}
            </button>

            <label className="mt-5 block text-xs font-medium text-slate-500">
              Preview
            </label>
            <div className="mt-1 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
              <iframe
                key={embedUrl}
                src={embedUrl}
                width="100%"
                height="180"
                frameBorder="0"
                style={{ border: "none", borderRadius: 12, maxWidth: 420 }}
                loading="lazy"
              />
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
